import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, QrCode, ScanLine, XCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fmtGNF } from "@/lib/format";
import { ABtn, AInput, ALabel, Panel } from "./AdminLayout";

export default function AdminPickupCheck() {
  const [code, setCode] = useState("");
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);

  const verify = async (value) => {
    const raw = (value ?? code).trim();
    if (!raw) return;
    setBusy(true);
    try {
      const { data } = await api.post("/warehouse/pickup/verify", { code: raw });
      setOrder(data);
    } catch (e) {
      setOrder(null);
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/warehouse/pickup/confirm", { code: order.pickup_code || code });
      toast.success(`Retrait validé — commande ${data.number} livrée`);
      setOrder({ ...order, already_used: true, status: "Livrée" });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const stopScan = async () => {
    setScanning(false);
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
      } catch (e) {
        /* caméra déjà arrêtée */
      }
      scannerRef.current = null;
    }
  };

  const startScan = async () => {
    setScanning(true);
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      const scanner = new Html5Qrcode("pickup-scanner");
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: 220 },
        async (text) => {
          setCode(text);
          await stopScan();
          verify(text);
        },
        () => {},
      );
    } catch (e) {
      setScanning(false);
      toast.error("Caméra indisponible : saisissez le code à 6 chiffres.");
    }
  };

  useEffect(() => () => { stopScan(); }, []);

  return (
    <div className="space-y-4" data-testid="admin-pickup-check-page">
      <Panel
        title="Vérifier un retrait"
        subtitle="Scannez le QR code du client ou saisissez son code à 6 chiffres."
        testid="pickup-check-panel"
      >
        <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
          <div>
            <ALabel>Code du bon de retrait</ALabel>
            <AInput
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              data-testid="pickup-code-input"
            />
          </div>
          <ABtn onClick={() => verify()} disabled={busy} data-testid="pickup-verify-button">
            <QrCode className="h-4 w-4" /> Vérifier
          </ABtn>
          <ABtn
            variant="gold"
            onClick={scanning ? stopScan : startScan}
            data-testid="pickup-scan-button"
          >
            <ScanLine className="h-4 w-4" /> {scanning ? "Arrêter" : "Scanner"}
          </ABtn>
        </div>
        <div
          id="pickup-scanner"
          className={`mt-3 overflow-hidden rounded-xl ${scanning ? "block" : "hidden"}`}
          data-testid="pickup-scanner"
        />
      </Panel>

      {order && (
        <Panel title={`Commande ${order.number}`} testid="pickup-order-panel">
          <div className="grid gap-2 text-sm sm:grid-cols-2">
            <p className="text-slate-300">Client : <span className="text-white">{order.customer_name}</span></p>
            <p className="font-num text-slate-300">Téléphone : <span className="text-white">{order.phone}</span></p>
            <p className="font-num text-slate-300">Total : <span className="text-emerald-400">{fmtGNF(order.total)}</span></p>
            <p className="text-slate-300">Statut : <span className="text-white">{order.status}</span></p>
            <p className="text-slate-300">
              Créneau : <span className="text-white">{order.pickup_date} · {order.pickup_slot}</span>
            </p>
            <p className="text-slate-300">
              Paiement :{" "}
              <span className={order.payment_ok ? "text-emerald-400" : "text-rose-400"}>{order.payment_status}</span>
            </p>
          </div>

          <ul className="mt-3 space-y-1 border-t border-slate-800 pt-3 text-sm text-slate-200">
            {order.items.map((it, i) => (
              <li key={i} className="flex justify-between gap-3">
                <span>{it.product_name} <span className="font-num text-slate-500">{it.sku}</span></span>
                <span className="font-num">× {it.quantity}</span>
              </li>
            ))}
          </ul>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            {order.already_used ? (
              <p className="flex items-center gap-2 text-sm text-slate-400" data-testid="pickup-already-used">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" /> Bon de retrait déjà utilisé
              </p>
            ) : order.payment_ok ? (
              <ABtn variant="gold" onClick={confirm} disabled={busy} data-testid="pickup-confirm-button">
                <CheckCircle2 className="h-4 w-4" /> Confirmer le retrait
              </ABtn>
            ) : (
              <p className="flex items-center gap-2 text-sm text-rose-400" data-testid="pickup-payment-blocked">
                <XCircle className="h-4 w-4" /> Paiement non confirmé : retrait impossible
              </p>
            )}
          </div>
        </Panel>
      )}
    </div>
  );
}
